/**
 * Zunelo Initial Domain Dataset
 * Rich dataset for Jira, GitHub PRs, Slack threads, Gmail messages, and AI Agents,
 * plus the Hold / Suggestion / External telemetry / PR-Slack cross-reference tables.
 *
 * This module is a fixture-data boundary: only files under src/services/clients/
 * may import from it (see AGENTS.md section 4.3). Static UI-only display config
 * (workflow step ordering, badge/filter definitions, daily standup send time) lives
 * in src/config/uiConfig.ts instead.
 */

import {
  AttentionItem,
  AgentItem,
  HoldInfo,
  StateSuggestion,
  SentryOrExtSignal,
  DailyGroup,
  GmailMessage,
  GmailThread,
} from '../domain/types';

export const HOLD: Record<string, HoldInfo> = {
  'DEV-2977': {
    kind: 'release',
    chip: 'espera release 2026.08.3',
    title: 'Aprobado, en cola para el release',
    note: 'El PR #401 está aprobado y en verde, pero no se mergea hasta el corte del release 2026.08.3. No es tu turno: solo hay que no perderlo de vista.',
    steps: [
      { label: 'Corte lun 18 ago', tone: 'flat' },
      { label: 'Sale mié 20 ago', tone: 'warn' },
      { label: 'Mergea solo Release Manager', tone: 'flat' },
    ],
  },
  'DEV-2802': {
    kind: 'infra',
    chip: 'bloqueado por Infra',
    title: 'Bloqueado fuera del equipo',
    note: 'Falta SNOWFLAKE_PRIVATE_KEY en el secret manager de staging. Security lo pasa por comité el jueves, o tramita excepción si mandas la justificación.',
    steps: [
      { label: '3 días parado', tone: 'bad' },
      { label: 'Comité jue 15 ago', tone: 'warn' },
      { label: 'Excepción posible hoy', tone: 'ok' },
    ],
  },
};

export const SUGGEST: Record<string, StateSuggestion> = {
  'DEV-3057': {
    to: 'Done',
    why: 'El PR #418 está aprobado, CI en verde y QA lo validó en staging hace 1 h. Sigue en Ready for QA desde ayer.',
  },
  'DEV-3040': {
    to: 'En curso',
    why: 'Llevas 6 h con commits en la rama y el ticket sigue en IN DEV. En el daily se ve como no empezado.',
  },
};

export const EXT: Record<string, SentryOrExtSignal[]> = {
  'DEV-3040': [
    {
      src: 'Sentry',
      label: 'CarrierPopup renderiza vacío',
      meta: '34 eventos · 24 h',
      tone: 'bad',
    },
  ],
  'DEV-3056': [
    {
      src: 'Sentry',
      label: 'ViolationRow · severity undefined',
      meta: '3 eventos · preview',
      tone: 'warn',
    },
  ],
  'DEV-2977': [{ src: 'Moesif', label: 'p95 de /sightings', meta: '1.8 s → 620 ms', tone: 'ok' }],
  'DEV-2802': [
    { src: 'AWS', label: 'Secrets Manager · staging', meta: 'clave ausente 3 d', tone: 'bad' },
  ],
  'DEV-2915': [
    { src: 'AWS', label: 'CodeDeploy · producción', meta: 'rollback automático', tone: 'bad' },
  ],
};

export const PRTALK: Record<
  string,
  Array<{ ch: string; text: string; when: string; id: string }>
> = {
  '#412': [
    {
      id: 'SL-eng-carrier',
      ch: '#eng-carrier',
      when: 'hace 3 h',
      text: 'Ana: «lo dejo bloqueado hasta que definamos el conteo total»',
    },
  ],
  '#395': [
    {
      id: 'SL-infra',
      ch: '#infra',
      when: 'hace 5 h',
      text: 'J. Marín: «hay ventana de mantenimiento esta tarde para la rotación»',
    },
  ],
  '#401': [
    {
      id: 'SL-eng-data',
      ch: '#eng-data',
      when: 'ayer',
      text: 'Ana: «lo miro mañana a primera hora»',
    },
  ],
};

export const AGENTS: AgentItem[] = [
  {
    name: 'Claude Code',
    ticket: 'DEV-3056',
    state: 'corriendo',
    running: true,
    branch: 'wt/carrier-severity',
    last: 'Actualizando CarrierPopup.test.tsx con los casos de severidad leve',
    elapsed: '12 min',
    tokens: '184k tok',
    cost: '$2.40',
    secondAction: 'Detener',
  },
  {
    name: 'Codex',
    ticket: 'DEV-3056',
    state: 'corriendo',
    running: true,
    branch: 'wt/carrier-severity-alt',
    last: 'Probando agregación en el backend en lugar de filtrar en cliente',
    elapsed: '4 min',
    tokens: '61k tok',
    cost: '$0.85',
    secondAction: 'Detener',
  },
  {
    name: 'OpenCode',
    ticket: 'DEV-2802',
    state: 'en pausa',
    running: false,
    branch: 'feat/snowflake-rsa',
    last: 'Esperando SNOWFLAKE_PRIVATE_KEY en el entorno de staging',
    elapsed: '2 h 10 min',
    tokens: '298k tok',
    cost: '$3.95',
    secondAction: 'Reanudar',
  },
];

export const ITEMS: AttentionItem[] = [
  // --- JIRA ITEMS (6) ---
  {
    id: 'DEV-3056',
    source: 'jira',
    ticket: 'DEV-3056',
    st: 'you',
    type: 'Story',
    primary: 'DEV-3056',
    secondary: 'Pull request · 5 pts · Alta',
    workflow: 'Pull request',
    age: 'hace 3 h',
    title:
      'Carrier Popup — Update VIN Details Severe Violation History to Violation History with Full Severity Coverage',
    ai: 'Product amplió el alcance a todas las severidades. El PR #412 ya lo implementa, pero Ana pidió cambios y el hilo de #eng-carrier espera que definas si las leves entran en el conteo total.',
    next: 'Responder a Ana en el PR #412',
    action: 'Responder en el PR',
    desc: 'El popup de carrier solo lista violaciones severas del VIN. Product pide mostrar el historial completo con todos los niveles de severidad, manteniendo el orden por fecha y permitiendo distinguir el nivel de un vistazo.',
    criteria: [
      {
        id: 'c-DEV-3056-1',
        status: 'done',
        text: 'El popup lista violaciones de todos los niveles de severidad',
      },
      {
        id: 'c-DEV-3056-2',
        status: 'done',
        text: 'Cada fila indica el nivel con color y etiqueta accesible',
      },
      {
        id: 'c-DEV-3056-3',
        status: 'open',
        text: 'El contador total define si incluye las leves — pendiente de decisión',
      },
      {
        id: 'c-DEV-3056-4',
        status: 'open',
        text: 'Los filtros de fecha siguen funcionando sin regresiones',
      },
    ],
    subtasks: [
      {
        id: 'sub-DEV-3056-1',
        key: 'DEV-3056-1',
        summary: 'Extender el query de violations a todos los niveles',
        status: 'Hecho',
      },
      {
        id: 'sub-DEV-3056-2',
        key: 'DEV-3056-2',
        summary: 'Componente de badge por severidad',
        status: 'Hecho',
      },
      {
        id: 'sub-DEV-3056-3',
        key: 'DEV-3056-3',
        summary: 'Actualizar tests del filtro',
        status: 'En curso',
      },
    ],
    labels: ['frontend', 'carrier', 'severity', 'needs-decision'],
  },
  {
    id: 'DEV-2802',
    source: 'jira',
    ticket: 'DEV-2802',
    st: 'blocked',
    type: 'Task',
    primary: 'DEV-2802',
    secondary: 'On Hold · 8 pts · Alta',
    workflow: 'On Hold',
    age: 'hace 2 d',
    title:
      'Snowflake RSA Migrations — Migrar autenticación de password a JWT con par de claves RSA en ports_dashboard_api',
    ai: 'Bloqueado del lado de Infra: la clave privada no está en el secret manager de staging, el check de integración falla y el correo de Security del lunes sigue sin respuesta.',
    next: 'Escalar en #infra citando el correo de Security',
    action: 'Escalar a Infra',
    desc: 'Sustituir la autenticación por contraseña del conector de Snowflake por JWT firmado con par de claves RSA, incluyendo rotación y carga de la clave privada desde el secret manager en los tres entornos.',
    criteria: [
      {
        id: 'c-DEV-2802-1',
        status: 'done',
        text: 'El conector firma el JWT con la clave privada RSA',
      },
      {
        id: 'c-DEV-2802-2',
        status: 'done',
        text: 'La contraseña desaparece de la configuración y del código',
      },
      {
        id: 'c-DEV-2802-3',
        status: 'blocked',
        text: 'La clave privada está cargada en staging — bloqueado por Infra',
      },
      {
        id: 'c-DEV-2802-4',
        status: 'open',
        text: 'La suite de integración pasa en staging y producción',
      },
    ],
    subtasks: [
      {
        id: 'sub-DEV-2802-1',
        key: 'DEV-2802-1',
        summary: 'Generar par de claves y documentar rotación',
        status: 'Hecho',
      },
      {
        id: 'sub-DEV-2802-2',
        key: 'DEV-2802-2',
        summary: 'Adaptar el cliente de Snowflake',
        status: 'Hecho',
      },
      {
        id: 'sub-DEV-2802-3',
        key: 'DEV-2802-3',
        summary: 'Cargar secreto en staging',
        status: 'Bloqueado',
      },
    ],
    labels: ['backend', 'security', 'snowflake', 'blocked'],
  },
  {
    id: 'DEV-3057',
    source: 'jira',
    ticket: 'DEV-3057',
    st: 'ready',
    type: 'Story',
    primary: 'DEV-3057',
    secondary: 'Ready for QA · 3 pts · Media',
    workflow: 'Ready for QA',
    age: 'hace 20 min',
    title: 'Carrier Overview Tab — Add Daily Sightings to Carrier Intelligence Overview',
    ai: 'Todo en verde: CI pasó los 24 tests, Diego aprobó el PR #418 y QA lo validó en staging. Solo queda mergear y mover el ticket a Done.',
    next: 'Merge del PR #418 y cerrar el ticket',
    action: 'Merge y cerrar',
    desc: 'Añadir la métrica de avistamientos diarios al bloque de Carrier Intelligence del tab de overview, con serie de los últimos 30 días y comparación contra la media del periodo.',
    criteria: [
      {
        id: 'c-DEV-3057-1',
        status: 'done',
        text: 'La tarjeta muestra avistamientos diarios de los últimos 30 días',
      },
      { id: 'c-DEV-3057-2', status: 'done', text: 'Comparación contra la media del periodo' },
      {
        id: 'c-DEV-3057-3',
        status: 'done',
        text: 'Estado vacío cuando el carrier no tiene avistamientos',
      },
    ],
    subtasks: [
      {
        id: 'sub-DEV-3057-1',
        key: 'DEV-3057-1',
        summary: 'Endpoint de agregación diaria',
        status: 'Hecho',
      },
      {
        id: 'sub-DEV-3057-2',
        key: 'DEV-3057-2',
        summary: 'Tarjeta en Carrier Intelligence',
        status: 'Hecho',
      },
    ],
    labels: ['frontend', 'carrier', 'analytics'],
  },
  {
    id: 'DEV-2977',
    source: 'jira',
    ticket: 'DEV-2977',
    st: 'them',
    type: 'Task',
    primary: 'DEV-2977',
    secondary: 'In QA · 2 pts · Media',
    workflow: 'In QA',
    age: 'hace 1 d',
    title: 'Cachear las respuestas del endpoint de sightings por carrier',
    ai: 'Tu parte está hecha. Ana se comprometió ayer a revisar el PR #401 y no ha habido movimiento en 26 horas.',
    next: 'Recordar la revisión a Ana',
    action: 'Recordar a Ana',
    desc: 'Cachear en Redis las respuestas del endpoint de sightings por carrier con invalidación por evento de ingesta, para bajar la latencia del overview.',
    criteria: [
      { id: 'c-DEV-2977-1', status: 'done', text: 'Las respuestas se cachean por carrier y rango' },
      {
        id: 'c-DEV-2977-2',
        status: 'done',
        text: 'La caché se invalida al llegar nuevos avistamientos',
      },
      { id: 'c-DEV-2977-3', status: 'open', text: 'Revisión aprobada' },
    ],
    subtasks: [
      {
        id: 'sub-DEV-2977-1',
        key: 'DEV-2977-1',
        summary: 'Capa de caché en el endpoint',
        status: 'Hecho',
      },
      {
        id: 'sub-DEV-2977-2',
        key: 'DEV-2977-2',
        summary: 'Invalidación por evento',
        status: 'Hecho',
      },
    ],
    labels: ['backend', 'performance'],
  },
  {
    id: 'DEV-2915',
    source: 'jira',
    ticket: 'DEV-2915',
    st: 'them',
    type: 'Task',
    primary: 'DEV-2915',
    secondary: 'Tareas por hacer · 2 pts · Baja',
    workflow: 'Tareas por hacer',
    age: 'hace 3 d',
    title: 'Unificar los estados vacíos de las tablas de Carrier',
    ai: 'No puedes empezar: Marta debía pasar las maquetas el miércoles y avisó por correo de que llegan el jueves. Propone arrancar con los estados del design system.',
    next: 'Confirmar con Marta si arrancas sin maquetas',
    action: 'Responder a Marta',
    desc: 'Unificar el estado vacío de todas las tablas de Carrier (sightings, violations, intelligence) con un único componente, texto y ilustración consistentes.',
    criteria: [
      {
        id: 'c-DEV-2915-1',
        status: 'open',
        text: 'Un solo componente de estado vacío para las tres tablas',
      },
      { id: 'c-DEV-2915-2', status: 'open', text: 'Copy revisado por Product' },
      { id: 'c-DEV-2915-3', status: 'open', text: 'Maquetas aprobadas por diseño' },
    ],
    subtasks: [
      {
        id: 'sub-DEV-2915-1',
        key: 'DEV-2915-1',
        summary: 'Inventario de estados vacíos actuales',
        status: 'Hecho',
      },
      {
        id: 'sub-DEV-2915-2',
        key: 'DEV-2915-2',
        summary: 'Componente unificado',
        status: 'Pendiente',
      },
    ],
    labels: ['frontend', 'design-system', 'carrier'],
  },
  {
    id: 'DEV-3040',
    source: 'jira',
    ticket: 'DEV-3040',
    st: 'you',
    type: 'Bug',
    primary: 'DEV-3040',
    secondary: 'IN DEV · 3 pts · Alta',
    workflow: 'IN DEV',
    age: 'hace 6 h',
    title: 'Carrier Popup se queda en blanco al cambiar de carrier con la petición en vuelo',
    ai: 'Sentry registra 34 eventos en 24 h y el arreglo está a medias en tu rama local. Es el bug con más impacto abierto del sprint.',
    next: 'Terminar el arreglo del race condition',
    action: 'Continuar el arreglo',
    desc: 'Al cambiar de carrier antes de que responda la petición anterior, el popup renderiza vacío porque la respuesta obsoleta sobrescribe el estado.',
    criteria: [
      { id: 'c-DEV-3040-1', status: 'done', text: 'Reproducido con red lenta' },
      {
        id: 'c-DEV-3040-2',
        status: 'open',
        text: 'Cancelar la petición anterior al cambiar de carrier',
      },
      { id: 'c-DEV-3040-3', status: 'open', text: 'Test de regresión del race condition' },
    ],
    subtasks: [
      {
        id: 'sub-DEV-3040-1',
        key: 'DEV-3040-1',
        summary: 'Abortar la petición en vuelo',
        status: 'En curso',
      },
      {
        id: 'sub-DEV-3040-2',
        key: 'DEV-3040-2',
        summary: 'Test de regresión',
        status: 'Pendiente',
      },
    ],
    labels: ['bug', 'frontend', 'carrier', 'sentry'],
  },

  // --- GITHUB PRS (7) ---
  {
    id: 'PR-412',
    source: 'github',
    role: 'author',
    ticket: 'DEV-3056',
    st: 'you',
    kind: 'Listo para revisión',
    refs: [{ key: 'DEV-3040', grade: 'explícita', why: 'Closes DEV-3040 en la descripción' }],
    primary: '#412',
    secondary: 'genlogs/carrier-web',
    age: 'hace 3 h',
    title: 'feat: cobertura completa de severidad en Violation History',
    action: 'Responder revisión',
    ai: 'CI en verde y 4 checks pasando, pero Ana bloqueó el merge con 2 comentarios sin resolver sobre el conteo total. Sonar marca 1 code smell nuevo.',
    next: 'Resolver los comentarios de Ana',
    body: [
      'Amplía el filtro de severidad de Violation History para incluir todos los niveles, no solo severe.',
      'El contador superior sigue usando el filtro antiguo: pendiente de confirmar con Product si las leves suman al total.',
    ],
    head: 'wt/carrier-severity',
    base: 'main',
    add: 248,
    del: 96,
    commits: '7 commits',
    files: [
      {
        filename: 'src/components/carrier/CarrierPopup.tsx',
        additions: 96,
        deletions: 42,
        changes: 138,
      },
      {
        filename: 'src/components/carrier/ViolationRow.tsx',
        additions: 74,
        deletions: 18,
        changes: 92,
      },
      { filename: 'src/api/violations.ts', additions: 48, deletions: 30, changes: 78 },
      { filename: 'src/__tests__/CarrierPopup.test.tsx', additions: 30, deletions: 6, changes: 36 },
    ],
    checks: [
      {
        name: 'GitHub Actions · build (node 20)',
        conclusion: 'ok',
        durationFormatted: '1m 42s',
        title: 'Compilación y bundle sin avisos',
        summaryLines: ['bundle: 1.24 MB (+8 KB)', 'sin warnings de TypeScript'],
      },
      {
        name: 'GitHub Actions · unit tests',
        conclusion: 'ok',
        durationFormatted: '58s',
        title: '24 tests, 24 pasan',
        summaryLines: ['CarrierPopup: 9 tests', 'ViolationRow: 7 tests', 'violations API: 8 tests'],
      },
      {
        name: 'SonarCloud',
        conclusion: 'warn',
        durationFormatted: '46s',
        title: 'Quality gate pasa con 1 code smell nuevo',
        summaryLines: [
          'Cobertura 84.2% (+1.1%)',
          'Code smell: complejidad 12 en CarrierPopup.tsx L96',
          'Duplicación 0.4%',
        ],
      },
      {
        name: 'Sentry Release Health',
        conclusion: 'ok',
        durationFormatted: '12s',
        title: 'Sin errores nuevos en preview',
        summaryLines: ['0 issues nuevos', 'crash-free sessions 99.8%'],
      },
    ],
    reviewers: [
      {
        user: { id: 101, login: 'anareyes', name: 'Ana Reyes', initials: 'AR', color: '#C86FD6' },
        state: 'Cambios solicitados',
        isCurrentUser: false,
      },
      {
        user: {
          id: 102,
          login: 'diegolopez',
          name: 'Diego López',
          initials: 'DL',
          color: 'var(--z-ok)',
        },
        state: 'Pendiente',
        isCurrentUser: false,
      },
    ],
    comments: [
      {
        id: 'comm-412-0',
        user: { id: 201, login: 'anareyes', name: 'Ana Reyes', initials: 'AR', color: '#C86FD6' },
        createdAt: '2026-08-14T09:00:00.000Z',
        path: 'CarrierPopup.tsx',
        line: 118,
        resolved: false,
        state: 'Sin resolver',
        diffHunk: [
          { type: 'ctx', content: ' const total = violations.filter(' },
          { type: 'del', content: "-  v => v.severity === 'severe'" },
          { type: 'add', content: '+  v => SEVERITY_LEVELS.includes(v.severity)' },
          { type: 'ctx', content: ' ).length;' },
        ],
        body: 'Aquí cambias el total sin que Product lo haya confirmado. ¿El contador debe incluir las leves o mantenemos solo severas arriba y el detalle completo abajo?',
      },
      {
        id: 'comm-412-1',
        user: { id: 202, login: 'anareyes', name: 'Ana Reyes', initials: 'AR', color: '#C86FD6' },
        createdAt: '2026-08-14T09:00:00.000Z',
        path: 'ViolationRow.tsx',
        line: 42,
        resolved: false,
        state: 'Sin resolver',
        body: 'El badge de severidad leve usa el mismo gris que el estado deshabilitado; conviene diferenciarlo para accesibilidad.',
      },
      {
        id: 'comm-412-2',
        user: {
          id: 203,
          login: 'diegolopez',
          name: 'Diego López',
          initials: 'DL',
          color: 'var(--z-ok)',
        },
        createdAt: '2026-08-13T09:00:00.000Z',
        path: 'violations.ts',
        line: 20,
        resolved: true,
        state: 'Resuelto',
        body: 'Buen apaño con el índice por fecha, así evitamos el sort en cliente.',
      },
    ],
    mergeTitle: 'Bloqueado por revisión',
    mergeText: 'Ana solicitó cambios. Resuelve los 2 comentarios para poder mergear.',
    mergeCta: 'Responder revisión',
  },
  {
    id: 'PR-418',
    source: 'github',
    role: 'author',
    ticket: 'DEV-3057',
    st: 'ready',
    kind: 'Listo para revisión',
    primary: '#418',
    secondary: 'genlogs/carrier-web',
    age: 'hace 20 min',
    title: 'feat: daily sightings en Carrier Intelligence Overview',
    action: 'Merge y cerrar',
    ai: 'Aprobado por Diego, los 3 checks en verde y sin conflictos con main. Nada bloquea el merge.',
    next: 'Hacer merge y cerrar DEV-3057',
    body: [
      'Añade la serie de avistamientos diarios de los últimos 30 días al bloque de Carrier Intelligence, con comparación contra la media del periodo.',
    ],
    head: 'wt/daily-sightings',
    base: 'main',
    add: 112,
    del: 18,
    commits: '3 commits',
    files: [
      {
        filename: 'src/components/carrier/IntelligenceOverview.tsx',
        additions: 68,
        deletions: 12,
        changes: 80,
      },
      { filename: 'src/api/sightings.ts', additions: 44, deletions: 6, changes: 50 },
    ],
    checks: [
      {
        name: 'GitHub Actions · build (node 20)',
        conclusion: 'ok',
        durationFormatted: '1m 38s',
        title: 'Compilación limpia',
        summaryLines: ['bundle: 1.23 MB (+2 KB)'],
      },
      {
        name: 'GitHub Actions · unit tests',
        conclusion: 'ok',
        durationFormatted: '52s',
        title: '24 tests, 24 pasan',
        summaryLines: ['IntelligenceOverview: 6 tests', 'sightings API: 5 tests'],
      },
      {
        name: 'SonarCloud',
        conclusion: 'ok',
        durationFormatted: '41s',
        title: 'Quality gate pasa sin incidencias',
        summaryLines: ['Cobertura 86.0% (+0.4%)', '0 code smells nuevos'],
      },
    ],
    reviewers: [
      {
        user: {
          id: 103,
          login: 'diegolopez',
          name: 'Diego López',
          initials: 'DL',
          color: 'var(--z-ok)',
        },
        state: 'Aprobado',
        isCurrentUser: false,
      },
    ],
    comments: [
      {
        id: 'comm-418-0',
        user: {
          id: 204,
          login: 'diegolopez',
          name: 'Diego López',
          initials: 'DL',
          color: 'var(--z-ok)',
        },
        createdAt: '2026-08-14T11:40:00.000Z',
        path: 'aprobación',
        resolved: true,
        state: 'Aprobado',
        body: 'Se ve bien, los números cuadran con el dashboard antiguo. Mergea cuando quieras.',
      },
    ],
    mergeTitle: 'Listo para mergear',
    mergeText: 'CI en verde, aprobado y sin conflictos con main.',
    mergeCta: 'Merge y cerrar DEV-3057',
  },
  {
    id: 'PR-395',
    source: 'github',
    role: 'author',
    ticket: 'DEV-2802',
    st: 'blocked',
    kind: 'Con conflictos',
    primary: '#395',
    secondary: 'genlogs/ports_dashboard_api',
    age: 'hace 2 d',
    title: 'feat: autenticación RSA JWT para Snowflake',
    action: 'Ver logs del check',
    ai: 'El check de integración falla por una credencial ausente en staging, no por el código. Sentry no registra errores nuevos y Sonar sube la cobertura.',
    next: 'Cargar SNOWFLAKE_PRIVATE_KEY en staging',
    body: [
      'Sustituye la autenticación por contraseña del conector de Snowflake por JWT firmado con par de claves RSA.',
      'Incluye rotación documentada y lectura de la clave privada desde el secret manager.',
    ],
    head: 'feat/snowflake-rsa',
    base: 'main',
    add: 412,
    del: 190,
    commits: '12 commits',
    files: [
      { filename: 'src/snowflake/client.py', additions: 168, deletions: 92, changes: 260 },
      { filename: 'src/snowflake/auth.py', additions: 144, deletions: 48, changes: 192 },
      { filename: 'tests/test_snowflake_auth.py', additions: 100, deletions: 50, changes: 150 },
    ],
    checks: [
      {
        name: 'GitHub Actions · build',
        conclusion: 'ok',
        durationFormatted: '2m 04s',
        title: 'Compilación correcta',
        summaryLines: ['python 3.12', 'wheel generado'],
      },
      {
        name: 'GitHub Actions · unit tests',
        conclusion: 'ok',
        durationFormatted: '1m 12s',
        title: '38 tests, 38 pasan',
        summaryLines: ['auth: 14 tests', 'client: 24 tests'],
      },
      {
        name: 'GitHub Actions · integration · snowflake',
        conclusion: 'fail',
        durationFormatted: '18s',
        title: 'Falla por credencial ausente, no por código',
        summaryLines: [
          'KeyError: SNOWFLAKE_PRIVATE_KEY',
          'entorno: staging',
          'último éxito: hace 3 días',
        ],
      },
      {
        name: 'SonarCloud',
        conclusion: 'ok',
        durationFormatted: '52s',
        title: 'Quality gate pasa',
        summaryLines: ['Cobertura 79.4% (+6.2%)', '2 code smells resueltos'],
      },
    ],
    reviewers: [
      {
        user: { id: 104, login: 'jmarin', name: 'J. Marín', initials: 'JM', color: '#4C8DFF' },
        state: 'Pendiente',
        isCurrentUser: false,
      },
    ],
    comments: [
      {
        id: 'comm-395-0',
        user: { id: 205, login: 'jmarin', name: 'J. Marín', initials: 'JM', color: '#4C8DFF' },
        createdAt: '2026-08-12T09:00:00.000Z',
        path: 'auth.py',
        line: 61,
        resolved: false,
        state: 'Sin resolver',
        body: '¿La rotación queda cubierta si la clave se carga desde el secret manager en caliente, o hace falta reiniciar el servicio?',
      },
    ],
    mergeTitle: 'CI en rojo',
    mergeText: 'integration · snowflake falla: falta SNOWFLAKE_PRIVATE_KEY en staging.',
    mergeCta: 'Ver logs del check',
  },
  {
    id: 'PR-401',
    source: 'github',
    role: 'author',
    ticket: 'DEV-2977',
    st: 'them',
    kind: 'Listo para revisión',
    primary: '#401',
    secondary: 'genlogs/ports_dashboard_api',
    age: 'hace 1 d',
    title: 'perf: cache de respuestas del endpoint de sightings',
    action: 'Recordar revisión',
    ai: 'Listo por tu parte: checks en verde desde ayer y sin comentarios. Solo falta que Ana revise.',
    next: 'Recordar la revisión a Ana',
    body: [
      'Cachea en Redis las respuestas del endpoint de sightings por carrier, con invalidación por evento de ingesta.',
    ],
    head: 'perf/sightings-cache',
    base: 'main',
    add: 86,
    del: 12,
    commits: '4 commits',
    files: [
      { filename: 'src/api/sightings.py', additions: 54, deletions: 8, changes: 62 },
      { filename: 'src/cache/redis.py', additions: 32, deletions: 4, changes: 36 },
    ],
    checks: [
      {
        name: 'GitHub Actions · build',
        conclusion: 'ok',
        durationFormatted: '1m 51s',
        title: 'Compilación correcta',
        summaryLines: ['python 3.12'],
      },
      {
        name: 'GitHub Actions · unit tests',
        conclusion: 'ok',
        durationFormatted: '1m 03s',
        title: '22 tests, 22 pasan',
        summaryLines: ['cache: 9 tests', 'sightings API: 13 tests'],
      },
      {
        name: 'Pendo',
        conclusion: 'ok',
        durationFormatted: '9s',
        title: 'Sin impacto en eventos instrumentados',
        summaryLines: ['0 eventos rotos', 'latencia p95 −180 ms en preview'],
      },
    ],
    reviewers: [
      {
        user: { id: 105, login: 'anareyes', name: 'Ana Reyes', initials: 'AR', color: '#C86FD6' },
        state: 'Pendiente',
        isCurrentUser: false,
      },
    ],
    comments: [],
    mergeTitle: 'Esperando revisión',
    mergeText: 'Ana tiene la revisión asignada desde ayer. Sin actividad en 26 h.',
    mergeCta: 'Recordar a Ana',
  },
  {
    id: 'PR-427',
    source: 'github',
    ticket: 'DEV-3072',
    st: 'you',
    kind: 'Listo para revisión',
    role: 'reviewer',
    refs: [
      { key: 'DEV-3057', grade: 'inferida', why: 'toca el mismo componente que acabas de cambiar' },
    ],
    primary: '#427',
    secondary: 'genlogs/carrier-web · abre Ana Reyes',
    age: 'hace 5 h',
    title: 'fix: normaliza husos horarios en la serie de Daily Sightings',
    action: 'Revisar el PR',
    ai: 'Ana te pidió revisión hace 5 h y eres el único revisor. Toca el mismo componente que acabas de cambiar en DEV-3057, así que el riesgo de conflicto es real.',
    next: 'Revisar antes de mergear DEV-3057',
    head: 'fix/sightings-timezone',
    base: 'main',
    add: 64,
    del: 22,
    commits: '2 commits',
    body: [
      'La serie diaria usaba la zona del navegador y desplazaba un día los avistamientos para carriers en UTC−7.',
      'Normaliza todo a UTC en el endpoint y formatea en cliente.',
    ],
    files: [
      { filename: 'src/api/sightings.ts', additions: 38, deletions: 14, changes: 52 },
      {
        filename: 'src/components/carrier/IntelligenceOverview.tsx',
        additions: 26,
        deletions: 8,
        changes: 34,
      },
    ],
    checks: [
      {
        name: 'GitHub Actions · build (node 20)',
        conclusion: 'ok',
        durationFormatted: '1m 40s',
        title: 'Compilación limpia',
        summaryLines: ['bundle: 1.23 MB (sin cambio)'],
      },
      {
        name: 'GitHub Actions · unit tests',
        conclusion: 'ok',
        durationFormatted: '55s',
        title: '26 tests, 26 pasan',
        summaryLines: ['sightings API: 8 tests nuevos'],
      },
      {
        name: 'SonarCloud',
        conclusion: 'ok',
        durationFormatted: '44s',
        title: 'Quality gate pasa',
        summaryLines: ['Cobertura 86.4% (+0.4%)', '0 code smells nuevos'],
      },
    ],
    reviewers: [
      {
        user: {
          id: 106,
          login: 'juanchez',
          name: 'Juan (tú)',
          initials: 'JU',
          color: 'var(--z-accent)',
        },
        state: 'Te piden revisión',
        isCurrentUser: true,
      },
    ],
    comments: [],
    mergeTitle: 'Espera tu revisión',
    mergeText: 'Eres el único revisor asignado. Ana no puede mergear sin tu aprobación.',
    mergeCta: 'Aprobar o comentar',
  },
  {
    id: 'PR-388',
    source: 'github',
    ticket: 'DEV-2990',
    st: 'you',
    kind: 'Con conflictos',
    role: 'reviewer',
    primary: '#388',
    secondary: 'genlogs/ports_dashboard_api · abre J. Marín',
    age: 'hace 2 d',
    title: 'chore: sube el runner de Python a 3.12 en el pipeline de datos',
    action: 'Revisar el PR',
    ai: 'Revisión pendiente desde hace 2 días y con conflictos en el lockfile. Comparte pipeline con tu migración RSA, así que conviene mirarlo antes de desbloquear DEV-2802.',
    next: 'Revisar y pedir rebase',
    head: 'chore/python-312',
    base: 'main',
    add: 148,
    del: 96,
    commits: '5 commits',
    body: [
      'Actualiza el runner y las dependencias del pipeline de datos a Python 3.12.',
      'Pendiente de rebase: el lockfile choca con feat/snowflake-rsa.',
    ],
    files: [
      { filename: 'pyproject.toml', additions: 42, deletions: 30, changes: 72 },
      { filename: 'poetry.lock', additions: 88, deletions: 60, changes: 148 },
      { filename: '.github/workflows/data.yml', additions: 18, deletions: 6, changes: 24 },
    ],
    checks: [
      {
        name: 'GitHub Actions · build',
        conclusion: 'ok',
        durationFormatted: '2m 12s',
        title: 'Compilación correcta en 3.12',
        summaryLines: ['python 3.12.4'],
      },
      {
        name: 'GitHub Actions · unit tests',
        conclusion: 'warn',
        durationFormatted: '1m 30s',
        title: '3 tests marcados como flaky',
        summaryLines: ['test_ingest_retry: 2 reintentos', 'test_cache_ttl: 1 reintento'],
      },
      {
        name: 'Sentry Release Health',
        conclusion: 'ok',
        durationFormatted: '11s',
        title: 'Sin errores nuevos',
        summaryLines: ['0 issues nuevos'],
      },
    ],
    reviewers: [
      {
        user: {
          id: 107,
          login: 'juanchez',
          name: 'Juan (tú)',
          initials: 'JU',
          color: 'var(--z-accent)',
        },
        state: 'Te piden revisión',
        isCurrentUser: true,
      },
      {
        user: { id: 108, login: 'anareyes', name: 'Ana Reyes', initials: 'AR', color: '#C86FD6' },
        state: 'Pendiente',
        isCurrentUser: false,
      },
    ],
    comments: [
      {
        id: 'comm-388-0',
        user: { id: 206, login: 'jmarin', name: 'J. Marín', initials: 'JM', color: '#4C8DFF' },
        createdAt: '2026-08-12T09:00:00.000Z',
        path: 'poetry.lock',
        resolved: false,
        state: 'Sin resolver',
        body: '@juan esto choca con tu rama de Snowflake. ¿Rebaseo yo o prefieres mergear la tuya primero?',
      },
    ],
    mergeTitle: 'Conflictos por resolver',
    mergeText: 'El lockfile choca con feat/snowflake-rsa. Necesita rebase y tu revisión.',
    mergeCta: 'Pedir rebase',
  },
  {
    id: 'PR-380',
    source: 'github',
    role: 'author',
    ticket: 'DEV-2915',
    st: 'blocked',
    kind: 'Mergeado',
    merged: true,
    primary: '#380',
    secondary: 'genlogs/carrier-web',
    age: 'ayer',
    title: 'chore: extrae EmptyState a un componente compartido',
    action: 'Ver logs del deploy',
    ai: 'Mergeado ayer y el deploy a staging pasó, pero el job de producción falló en migraciones y CodeDeploy hizo rollback. Nadie lo ha mirado en 14 h.',
    next: 'Revisar el fallo del deploy a producción',
    head: 'chore/empty-state',
    base: 'main',
    add: 132,
    del: 210,
    commits: '6 commits',
    body: ['Extrae el estado vacío repetido en tres tablas de Carrier a un componente compartido.'],
    files: [
      {
        filename: 'src/components/common/EmptyState.tsx',
        additions: 96,
        deletions: 0,
        changes: 96,
      },
      {
        filename: 'src/components/carrier/SightingsTable.tsx',
        additions: 18,
        deletions: 70,
        changes: 88,
      },
      {
        filename: 'src/components/carrier/ViolationsTable.tsx',
        additions: 18,
        deletions: 70,
        changes: 88,
      },
    ],
    checks: [
      {
        name: 'GitHub Actions · build (node 20)',
        conclusion: 'ok',
        durationFormatted: '1m 35s',
        title: 'Compilación limpia',
        summaryLines: ['bundle: 1.22 MB (−9 KB)'],
      },
      {
        name: 'GitHub Actions · unit tests',
        conclusion: 'ok',
        durationFormatted: '49s',
        title: '24 tests, 24 pasan',
        summaryLines: ['EmptyState: 5 tests nuevos'],
      },
      {
        name: 'Deploy · staging',
        conclusion: 'ok',
        durationFormatted: '3m 12s',
        title: 'Desplegado y con smoke tests en verde',
        summaryLines: ['versión 2026.08.2-rc4', 'smoke tests 12/12'],
      },
      {
        name: 'Deploy · producción',
        conclusion: 'fail',
        durationFormatted: '1m 04s',
        title: 'Falla en el paso de migraciones y hace rollback',
        summaryLines: [
          'alembic: relation "empty_state_config" already exists',
          'CodeDeploy: rollback automático',
          'reintento manual pendiente',
        ],
      },
    ],
    reviewers: [
      {
        user: {
          id: 109,
          login: 'diegolopez',
          name: 'Diego López',
          initials: 'DL',
          color: 'var(--z-ok)',
        },
        state: 'Aprobado',
        isCurrentUser: false,
      },
    ],
    comments: [
      {
        id: 'comm-380-0',
        user: {
          id: 207,
          login: 'diegolopez',
          name: 'Diego López',
          initials: 'DL',
          color: 'var(--z-ok)',
        },
        createdAt: '2026-08-12T09:00:00.000Z',
        path: 'EmptyState.tsx',
        line: 12,
        resolved: true,
        state: 'Resuelto',
        body: 'Falta aria-label en el icono ilustrativo, si no el lector de pantalla lo lee como imagen suelta.',
      },
      {
        id: 'comm-380-1',
        user: {
          id: 208,
          login: 'juanchez',
          name: 'Juan (tú)',
          initials: 'JU',
          color: 'var(--z-accent)',
        },
        createdAt: '2026-08-13T09:00:00.000Z',
        path: 'EmptyState.tsx',
        line: 12,
        resolved: true,
        state: 'Resuelto',
        body: 'Hecho: el icono va con aria-hidden y el texto hace de etiqueta.',
      },
    ],
    mergeTitle: 'Mergeado · deploy en rojo',
    mergeText:
      'Mergeado ayer a main. El job de producción falló en migraciones y revirtió; staging sí quedó desplegado.',
    mergeCta: 'Ver logs del deploy',
  },

  // --- SLACK THREADS (4) ---
  {
    id: 'SL-eng-carrier',
    source: 'slack',
    ticket: 'DEV-3056',
    st: 'you',
    refs: [
      { key: 'DEV-3040', grade: 'explícita', why: 'clave citada en el hilo' },
      { key: 'DEV-3057', grade: 'inferida', why: 'Ana habla del mismo despliegue' },
    ],
    tags: ['Atención', 'Menciones', 'Decisiones'],
    kind: 'Hilo',
    primary: '#eng-carrier',
    secondary: 'mención directa · 4 mensajes',
    age: 'hace 3 h',
    title: '«¿Las violaciones leves entran en el conteo total o van aparte?»',
    ai: 'Product ya definió el alcance, pero falta tu criterio sobre el conteo total. El hilo lleva 3 h parado y con él el PR #412.',
    next: 'Responder a Ana en el hilo',
    threadMeta: 'Carrier web · frontend y datos de carriers',
    messages: [
      {
        ts: '1723630680.000100',
        user: { id: 'ULV', name: 'l.vega', realName: 'L. Vega', initials: 'LV', color: '#4C8DFF' },
        text: 'Product confirma: queremos ver el historial completo de violaciones, no solo severas. Lo dejé escrito en DEV-3056.',
        timeFormatted: '09:12',
        isoTime: '2026-08-14T09:12:00.000Z',
        reactions: [{ name: '👍', count: 3 }],
      },
      {
        ts: '1723630680.000101',
        user: {
          id: 'UAR',
          name: 'ana.reyes',
          realName: 'Ana Reyes',
          initials: 'AR',
          color: '#C86FD6',
        },
        text: 'Ya está implementado en el PR #412, pero el contador de arriba sigue sumando solo las severas.',
        timeFormatted: '09:40',
        isoTime: '2026-08-14T09:40:00.000Z',
        reactions: [],
        repliesCount: 2,
        lastReplyTime: 'hace 4 h',
      },
      {
        ts: '1723630680.000102',
        user: {
          id: 'UAR',
          name: 'ana.reyes',
          realName: 'Ana Reyes',
          initials: 'AR',
          color: '#C86FD6',
        },
        text: '@juan ¿las leves entran en el conteo total? Lo dejo bloqueado hasta que me digas.',
        timeFormatted: '11:58',
        isoTime: '2026-08-14T11:58:00.000Z',
        tag: 'te menciona',
        reactions: [{ name: '👀', count: 2 }],
        divider: true,
        codeSnippet: "severityFilter: ['severe']  // ← ¿lo abrimos a todos los niveles?",
      },
    ],
  },
  {
    id: 'SL-infra',
    source: 'slack',
    ticket: 'DEV-2802',
    st: 'you',
    refs: [{ key: 'DEV-2977', grade: 'inferida', why: 'mismo endpoint de sightings' }],
    tags: ['Atención', 'Decisiones', 'Seguimiento'],
    kind: 'Hilo',
    primary: '#infra',
    secondary: 'hilo · 7 mensajes',
    age: 'hace 5 h',
    title: '«¿Alguien necesita la rotación de claves de Snowflake para hoy?»',
    action: 'Escalar aquí',
    ai: 'Infra abrió ventana de mantenimiento para hoy: es la oportunidad de desbloquear DEV-2802 sin esperar al comité del jueves.',
    next: 'Pedir la carga del secreto en esta ventana',
    threadMeta: 'Infraestructura, secretos y despliegues',
    messages: [
      {
        ts: '1723630680.000110',
        user: {
          id: 'UJM',
          name: 'j.marin',
          realName: 'J. Marín',
          initials: 'JM',
          color: '#4C8DFF',
        },
        text: '¿Alguien necesita la rotación de claves de Snowflake para hoy? Tenemos ventana de mantenimiento esta tarde.',
        timeFormatted: '07:30',
        isoTime: '2026-08-14T07:30:00.000Z',
        reactions: [{ name: '🙋', count: 1 }],
        repliesCount: 5,
        lastReplyTime: 'hace 1 h',
      },
      {
        ts: '1723630680.000111',
        user: {
          id: 'UIN',
          name: 'infra.bot',
          realName: 'Infra bot',
          initials: 'IN',
          color: 'var(--z-warn)',
        },
        text: 'Recordatorio: los secretos de staging se recargan solo tras un deploy manual.',
        timeFormatted: '07:31',
        isoTime: '2026-08-14T07:31:00.000Z',
        reactions: [],
      },
    ],
  },
  {
    id: 'SL-product-demo',
    source: 'slack',
    ticket: 'DEV-3057',
    st: 'ready',
    tags: ['Seguimiento', 'Tu ámbito'],
    kind: 'Hilo',
    primary: '#product-demo',
    secondary: 'hilo · 3 mensajes',
    age: 'hace 1 h',
    title: 'QA: «validado en staging, los sightings diarios cuadran»',
    action: 'Cerrar el ticket',
    ai: 'Validación cerrada por QA. No requiere respuesta; es la última señal antes de cerrar DEV-3057.',
    next: 'Cerrar DEV-3057',
    threadMeta: 'Demos y validaciones de producto',
    messages: [
      {
        ts: '1723630680.000120',
        user: {
          id: 'UQA',
          name: 'qa.sofia',
          realName: 'QA · Sofía',
          initials: 'QA',
          color: 'var(--z-ok)',
        },
        text: 'Validado en staging, los sightings diarios cuadran con el dashboard antiguo.',
        timeFormatted: '05:44',
        isoTime: '2026-08-14T05:44:00.000Z',
        reactions: [{ name: '✅', count: 4 }],
      },
    ],
  },
  {
    id: 'SL-eng-data',
    source: 'slack',
    ticket: 'DEV-2977',
    st: 'them',
    tags: ['Menciones', 'Seguimiento'],
    kind: 'Hilo',
    primary: '#eng-data',
    secondary: 'mención · 2 mensajes',
    age: 'ayer',
    title: 'Ana: «lo miro mañana a primera hora»',
    action: 'Recordar a Ana',
    ai: 'Compromiso de ayer sin cumplir: 26 h sin actividad en el PR #401.',
    next: 'Recordar a Ana hoy',
    threadMeta: 'Pipelines y APIs de datos',
    messages: [
      {
        ts: '1723630680.000130',
        user: {
          id: 'UAR',
          name: 'ana.reyes',
          realName: 'Ana Reyes',
          initials: 'AR',
          color: '#C86FD6',
        },
        text: 'Lo miro mañana a primera hora, hoy ya no me da la vida.',
        timeFormatted: 'ayer 18:20',
        isoTime: '2026-08-13T18:20:00.000Z',
        reactions: [{ name: '🙏', count: 1 }],
      },
    ],
  },
];

/**
 * Raw Gmail message fixtures, typed against the real GmailMessage domain contract.
 * Consumed exclusively by MockGmailClient (see src/services/clients/mockGmailClient.ts).
 */
export const INITIAL_GMAIL_MESSAGES: GmailMessage[] = [
  {
    id: 'ML-security',
    threadId: 'thread-security-1',
    from: {
      name: 'Security · genlogs',
      email: 'security@genlogs.io',
      initials: 'SE',
      color: 'var(--z-bad)',
    },
    to: 'ti, infra@genlogs.io',
    subject: 'Re: rotación de claves para ports_dashboard_api',
    bodyParagraphs: [
      'Hola Juan, hemos revisado la petición de par de claves RSA para ports_dashboard_api.',
      'La generación está aprobada, pero la carga en el secret manager de staging queda pendiente de que el rotation policy pase por el comité de seguridad. La próxima sesión es el jueves.',
      'Si necesitas desbloquear antes, respóndenos con la justificación de negocio y lo tramitamos como excepción.',
    ],
    quotedHead: 'El 10 ago escribiste:',
    quotedText:
      '«Necesitamos la clave privada en staging para poder correr la suite de integración de DEV-2802…»',
    kind: 'Interno',
    unread: true,
    blocksWork: true,
    linkedTicketKey: 'DEV-2802',
    receivedAt: '2026-08-12T09:00:00.000Z',
    aiSummary: {
      summary:
        'Security ofrece tramitar una excepción si respondes con la justificación de negocio: es el camino corto para desbloquear DEV-2802.',
      actionItem: 'Responder con la justificación',
    },
  },
  {
    id: 'ML-snowflake',
    threadId: 'thread-snowflake-1',
    from: {
      name: 'Snowflake',
      email: 'snowflake-noreply@snowflake.com',
      initials: 'SN',
      color: '#4C8DFF',
    },
    to: 'ti',
    subject: 'Mantenimiento programado: sábado 15, 02:00–04:00 UTC',
    bodyParagraphs: [
      'El sábado 15 de agosto entre las 02:00 y las 04:00 UTC realizaremos mantenimiento en la región donde vive tu cuenta.',
      'Los entornos de staging pueden rechazar conexiones durante la ventana. Recomendamos reprogramar cargas y pruebas de integración.',
    ],
    quotedHead: 'Detalle de la ventana:',
    quotedText: 'Región AWS us-east-1 · staging y desarrollo · producción no afectada.',
    kind: 'Aviso',
    unread: true,
    blocksWork: false,
    linkedTicketKey: 'DEV-2802',
    receivedAt: '2026-08-14T06:00:00.000Z',
    aiSummary: {
      summary:
        'La ventana afecta al staging donde tienes que validar la migración RSA. Conviene avisar antes del viernes.',
      actionItem: 'Avisar en #infra',
    },
  },
  {
    id: 'ML-marta',
    threadId: 'thread-marta-1',
    from: {
      name: 'Marta Cano',
      email: 'marta.cano@genlogs.io',
      initials: 'MC',
      color: 'var(--z-warn)',
    },
    to: 'ti, diseño@genlogs.io',
    subject: 'Maquetas de estados vacíos — voy con retraso',
    bodyParagraphs: [
      'Juan, las maquetas de estados vacíos de las tablas de Carrier se me han cruzado con la revisión de la app móvil.',
      'Te las paso el jueves sin falta. Si necesitas empezar antes, usa los estados del design system y ya ajustamos después.',
    ],
    quotedHead: 'El 8 ago escribiste:',
    quotedText:
      '«¿Para cuándo tendrías las maquetas de los estados vacíos? DEV-2915 depende de ellas.»',
    kind: 'Interno',
    unread: false,
    blocksWork: false,
    linkedTicketKey: 'DEV-2915',
    receivedAt: '2026-08-11T12:00:00.000Z',
    aiSummary: {
      summary:
        'Marta propone que arranques con los estados del design system en lugar de esperar al jueves.',
      actionItem: 'Confirmar el plan con Marta',
    },
  },
];

export const INITIAL_GMAIL_THREADS: GmailThread[] = [
  {
    id: 'thread-security-1',
    subject: 'Re: rotación de claves para ports_dashboard_api',
    unread: true,
    blocksWork: true,
    kind: 'Interno',
    receivedAt: '2026-08-12T09:00:00.000Z',
    linkedTicketKey: 'DEV-2802',
    messages: [
      {
        id: 'ML-security-sent-1',
        threadId: 'thread-security-1',
        from: {
          name: 'Juan Chez',
          email: 'ti@genlogs.io',
          initials: 'JC',
          color: 'var(--z-accent)',
        },
        to: 'security@genlogs.io',
        subject: 'Rotación de claves para ports_dashboard_api',
        bodyParagraphs: [
          'Hola equipo de seguridad, necesitamos la clave privada en staging para poder correr la suite de integración de DEV-2802.',
          '¿Podrían gestionar la rotación y el par de claves RSA?',
        ],
        kind: 'Interno',
        unread: false,
        blocksWork: false,
        receivedAt: '2026-08-10T15:30:00.000Z',
      },
      INITIAL_GMAIL_MESSAGES[0],
    ],
  },
  {
    id: 'thread-snowflake-1',
    subject: INITIAL_GMAIL_MESSAGES[1].subject,
    unread: true,
    blocksWork: false,
    kind: 'Aviso',
    receivedAt: INITIAL_GMAIL_MESSAGES[1].receivedAt,
    linkedTicketKey: INITIAL_GMAIL_MESSAGES[1].linkedTicketKey,
    messages: [INITIAL_GMAIL_MESSAGES[1]],
  },
  {
    id: 'thread-marta-1',
    subject: INITIAL_GMAIL_MESSAGES[2].subject,
    unread: false,
    blocksWork: false,
    kind: 'Interno',
    receivedAt: INITIAL_GMAIL_MESSAGES[2].receivedAt,
    linkedTicketKey: INITIAL_GMAIL_MESSAGES[2].linkedTicketKey,
    messages: [INITIAL_GMAIL_MESSAGES[2]],
  },
];

export const DAILY_CONFIG: DailyGroup[] = [
  {
    id: 'ayer',
    title: 'Ayer',
    tone: 'ok',
    lines: [
      {
        id: 'd-1',
        key: 'DEV-3057',
        text: 'Terminé daily sightings; Diego aprobó el PR #418 y QA lo validó en staging',
        active: true,
        link: 'DEV-3057',
      },
      {
        id: 'd-2',
        key: 'PR #412',
        text: 'Subí la cobertura completa de severidad a revisión',
        active: true,
        link: 'PR-412',
      },
      {
        id: 'd-3',
        key: 'DEV-3040',
        text: 'Reproduje el render vacío del popup con red lenta',
        active: true,
        link: 'DEV-3040',
      },
    ],
    draftText: '',
  },
  {
    id: 'hoy',
    title: 'Hoy',
    tone: 'accent',
    lines: [
      {
        id: 'd-4',
        key: 'DEV-3056',
        text: 'Cierro con Product si las leves suman al total y respondo a Ana',
        active: true,
        link: 'DEV-3056',
      },
      {
        id: 'd-5',
        key: 'PR #427',
        text: 'Reviso el PR de husos horarios antes de que choque con lo mío',
        active: true,
        link: 'PR-427',
      },
      {
        id: 'd-6',
        key: 'DEV-3040',
        text: 'Termino el abort de la petición y el test de regresión',
        active: true,
        link: 'DEV-3040',
      },
    ],
    draftText: '',
  },
  {
    id: 'bloq',
    title: 'Bloqueos',
    tone: 'bad',
    lines: [
      {
        id: 'd-7',
        key: 'DEV-2802',
        text: 'Sigo sin la clave RSA en staging — comité de Security el jueves',
        active: true,
        link: 'DEV-2802',
      },
      {
        id: 'd-8',
        key: 'DEV-2977',
        text: 'PR #401 aprobado pero en cola hasta el release del miércoles',
        active: false,
        link: 'DEV-2977',
      },
    ],
    draftText: '',
  },
];

export const INITIAL_REMINDERS = [
  {
    id: 'rem-1',
    text: 'DEV-3057: mover a Done — el PR #418 ya está aprobado y QA lo validó',
    done: false,
    link: 'DEV-3057',
    due: '',
    time: '',
  },
  {
    id: 'rem-2',
    text: 'DEV-3040: mover a En curso — llevas 6 h con commits y sigue en IN DEV',
    done: false,
    link: 'DEV-3040',
    due: '2026-08-14',
    time: '17:00',
  },
];

export const INITIAL_NOTES = [
  {
    id: 'note-1',
    text: 'Revisar con Security el jueves 15:00 la excepción para SNOWFLAKE_PRIVATE_KEY',
    link: 'DEV-2802',
    createdAt: '2026-08-14T08:00:00.000Z',
  },
  {
    id: 'note-2',
    text: 'Documentar endpoint BFF para métricas de atención',
    link: 'DEV-3040',
    createdAt: '2026-08-14T09:00:00.000Z',
  },
];

// Aliases
export const HOLD_DATA = HOLD;
export const SUGGEST_DATA = SUGGEST;
export const EXT_SIGNALS = EXT;
export const PR_TALK_MAP = PRTALK;
export const INITIAL_DAILY_GROUPS = DAILY_CONFIG;
